import { useEffect, useRef, useState } from "react";

/**
 * Returns the prefix of `target` to show now, revealing streamed text at a steady
 * display rate instead of in network-sized bursts.
 */

// Advances are delta-time based against a 60Hz reference frame, so the reveal
// feels identical regardless of the display's actual refresh rate.
const CHARS_PER_SECOND = 60;
const CATCHUP_DIVISOR = 8;
const REFERENCE_HZ = 60;

export function useSmoothText(
  target: string,
  {
    enabled,
    charsPerSecond = CHARS_PER_SECOND,
    catchUp = true,
  }: {
    enabled: boolean;
    charsPerSecond?: number;
    /** Off for text that arrives whole, so it types at a steady pace instead of bursting. */
    catchUp?: boolean;
  }
): string {
  const [displayedLength, setDisplayedLength] = useState(() => (enabled ? 0 : target.length));

  // Read by the rAF loop so it isn't re-created on every reveal step.
  const displayedLengthRef = useRef(displayedLength);
  displayedLengthRef.current = displayedLength;

  const frameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number | null>(null);

  // Carries sub-character progress between frames so a slow trickle reveals at
  // exactly charsPerSecond rather than rounding up to one char per frame.
  const revealBudgetRef = useRef(0);

  const cancelFrame = (): void => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    lastFrameTimeRef.current = null;
    revealBudgetRef.current = 0;
  };

  // Snap to full when reveal is off or the target shrank; growth is handled below.
  useEffect(() => {
    const shouldSnap = !enabled || target.length < displayedLengthRef.current;
    if (!shouldSnap) return;

    cancelFrame();
    if (displayedLengthRef.current !== target.length) {
      setDisplayedLength(target.length);
    }
  }, [enabled, target]);

  useEffect(() => {
    if (!enabled) return;
    if (displayedLengthRef.current >= target.length) return;

    const step = (now: number): void => {
      const previousFrameTime = lastFrameTimeRef.current ?? now;
      const elapsedSeconds = Math.max(0, (now - previousFrameTime) / 1000);
      lastFrameTimeRef.current = now;

      const current = displayedLengthRef.current;
      const remaining = target.length - current;
      if (remaining <= 0) {
        cancelFrame();
        return;
      }

      const baseAdvance = charsPerSecond * elapsedSeconds;
      // Drains a fixed fraction of the backlog per reference frame, as a rate, so
      // bursts catch up at the same wall-clock pace on any refresh rate.
      const referenceFrames = elapsedSeconds * REFERENCE_HZ;
      const catchUpAdvance = catchUp
        ? remaining * (1 - Math.pow(1 - 1 / CATCHUP_DIVISOR, referenceFrames))
        : 0;

      revealBudgetRef.current += Math.max(baseAdvance, catchUpAdvance);
      const advance = Math.min(remaining, Math.floor(revealBudgetRef.current));
      if (advance <= 0) {
        frameRef.current = requestAnimationFrame(step);
        return;
      }
      revealBudgetRef.current -= advance;

      const next = current + advance;
      setDisplayedLength(next);

      if (next < target.length) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        cancelFrame();
      }
    };

    frameRef.current = requestAnimationFrame(step);
    return cancelFrame;
  }, [enabled, target, charsPerSecond, catchUp]);

  useEffect(() => cancelFrame, []);

  if (!enabled) return target;
  return target.slice(0, Math.min(displayedLength, target.length));
}
